import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DollarOutlined,
  CloseOutlined,
  DownOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { Badge } from '@/components/Badge'
import { SearchBox } from '@/components/SearchBox'
import { FilterDropdown } from '@/components/FilterDropdown'
import { Pagination } from '@/components/Pagination'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { useAuth } from '@/app/AuthContext'
import {
  BACKEND_STATUS_MAP,
  formatRupiah,
  formatPeriodeLabel,
  getStatusVariant,
  getStatusLabel,
  IURAN_TYPE_OPTIONS,
  parseMoney,
  getDueTypeLabel,
  getDueTypeVariant,
} from './iuranTypes'
import { DueCreate } from './DueCreate'
import { IuranDetail } from './IuranDetail'
import { IuranPayment } from './IuranPayment'
import type { IuranBill, IuranStatus, DueType } from './iuranTypes'
import { apiListBills, apiListPayments, apiListDues, apiUpdateDue, apiDeactivateDue, apiGetDueMonths, apiUpdateDuePeriodStatus, type DueMonthSummary, type ApiBill, type ApiDue, apiGenerateBills, getSessionPair } from '@/app/api'

type FilterType = 'semua' | 'status' | 'pembayaran' | 'periode' | 'jenis'

export type PaymentFilterStatus = 'pending' | 'approved' | 'rejected'

export const PAYMENT_STATUS_OPTIONS: { value: PaymentFilterStatus; label: string }[] = [
  { value: 'pending', label: 'Tertunda' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
]

const STATUS_OPTIONS: { value: IuranStatus; label: string }[] = [
  { value: 'belum_bayar', label: 'Belum Bayar' },
  { value: 'sebagian', label: 'Sebagian' },
  { value: 'lunas', label: 'Lunas' },
  { value: 'dibatalkan', label: 'Dibatalkan' },
  { value: 'sukarela', label: 'Sukarela' },
]

const statusFilterOptions: { value: FilterType; label: string }[] = [
  { value: 'semua', label: 'Semua' },
  { value: 'status', label: 'Status' },
  { value: 'pembayaran', label: 'Status Pembayaran' },
  { value: 'periode', label: 'Periode' },
  { value: 'jenis', label: 'Jenis Iuran' },
]

export function IuranList() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allBills, setAllBills] = useState<IuranBill[]>([])
  const [allPeriodesSet, setAllPeriodesSet] = useState<Set<string>>(new Set())

  // Create Due modal state
  const [showCreateDue, setShowCreateDue] = useState(false)

  // Detail modal state
  const [showDetailBillId, setShowDetailBillId] = useState<string | null>(null)

  // Payment modal state
  const [showPaymentBillId, setShowPaymentBillId] = useState<string | null>(null)

  // URL search params (must be read before any code that uses searchParams)
  const [searchParams, setSearchParams] = useSearchParams()

  // Auth context (must be before any code that uses `user`)
  const { user } = useAuth()

  // Tab state: 'tagihan' = "Iuran Saya" (personal bills), 'list-iuran' = "Iuran Warga" (all RT bills),
  // 'kelola-iuran' = "Kelola Iuran" (dues configuration)
  type ActiveTab = 'list-iuran' | 'tagihan' | 'kelola-iuran'
  const initialTab = (() => {
    const raw = searchParams.get('tab')
    if (raw === 'kelola-iuran') return 'kelola-iuran' as const
    if (raw === 'list-iuran') return 'list-iuran' as const
    // Default landing page is "Iuran Saya" (personal bills), including for managers.
    // Legacy links using ?tab=tagihan continue to resolve to this same default.
    return 'tagihan' as const
  })()

  // ═══════════════════════════════════════════════════
  // Iuran Tab Access Control
  // ═══════════════════════════════════════════════════

  function isWarga(u: {
    systemRole?: string | null
    role?: string
    jabatan?: string | null
  }): boolean {
    if (u.systemRole === 'super_admin') return false
    if (u.role !== 'warga') return false
    // A warga with a management jabatan is not a plain warga for tab purposes.
    // They hold an RT position (ketua, wakil_ketua, sekretaris, bendahara) and
    // should see the full iuran tab navigation, not be clamped to "Iuran Saya".
    const jabatan = (u.jabatan ?? '').trim().toLowerCase()
    const managementJabatans = new Set(['ketua', 'wakil_ketua', 'sekretaris', 'bendahara'])
    if (managementJabatans.has(jabatan)) return false
    return true
  }

  function canAccessIuranTabs(u: {
    systemRole?: string | null
    role?: string
    jabatan?: string | null
    effectivePermissions?: string[]
  }): boolean {
    if (u.systemRole === 'super_admin') return true
    if (Array.isArray(u.effectivePermissions) && u.effectivePermissions.includes('finance.read')) {
      return true
    }
    if (typeof u.jabatan === 'string' && u.jabatan.length > 0) {
      const accessJabatans = new Set(['ketua', 'wakil_ketua', 'sekretaris', 'bendahara'])
      return accessJabatans.has(u.jabatan)
    }
    switch (u.role) {
      case 'pengurus':
      case 'bendahara':
      case 'super_admin':
        return true
      default:
        return false
    }
  }

  const hasIuranAccess = user ? canAccessIuranTabs(user) : false
  const userIsWarga = user ? isWarga(user) : false

  // Force default tab for users without Iuran access, and ALWAYS force "Iuran Saya"
  // for warga — they must never land on a tab they cannot see.
  const effectiveInitialTab: ActiveTab = hasIuranAccess && !userIsWarga
    ? initialTab
    : 'tagihan'
  const [activeTab, setActiveTab] = useState<ActiveTab>(effectiveInitialTab)

  // Guard against deep links (?tab=list-iuran) or late-resolved auth state:
  // if the user is warga, clamp back to "Iuran Saya".
  useEffect(() => {
    if (userIsWarga && activeTab !== 'tagihan') {
      setActiveTab('tagihan')
    }
  }, [userIsWarga, activeTab])

  // Dues data for "Kelola Iuran" tab
  const [dues, setDues] = useState<ApiDue[]>([])
  const [duesLoading, setDuesLoading] = useState(false)
  const [duesError, setDuesError] = useState<string | null>(null)

  // Edit Due modal state
  const [editDueId, setEditDueId] = useState<string | null>(null)
  const [editDueForm, setEditDueForm] = useState<{ name: string; amount: string; period_type: 'monthly' | 'yearly' | 'one_time'; payment_rule: 'full' | 'partial'; is_voluntary: boolean; is_active: boolean; valid_until: string; due_type?: string; year?: number | null } | null>(null)
  const [editDueLoading, setEditDueLoading] = useState(false)
  const [dueMonths, setDueMonths] = useState<DueMonthSummary[]>([])
  const [dueMonthsLoading, setDueMonthsLoading] = useState(false)
  const [dueMonthsError, setDueMonthsError] = useState<string | null>(null)
  const [initialMonthStatus, setInitialMonthStatus] = useState<Record<string, boolean>>({})
  const [pendingMonthChanges, setPendingMonthChanges] = useState<Record<string, boolean>>({})
  const [syncBillsLoading, setSyncBillsLoading] = useState(false)
  const [syncBillsMessage, setSyncBillsMessage] = useState<string | null>(null)
  const [showSyncConfirm, setShowSyncConfirm] = useState(false)

  // Deactivate due state
  const [deactivateDueId, setDeactivateDueId] = useState<string | null>(null)
  const [deactivateLoading, setDeactivateLoading] = useState(false)

  const currentYear = String(new Date().getFullYear())
  const initialSearch = searchParams.get('q') || searchParams.get('search') || ''
  const initialFilter = (searchParams.get('filter') as FilterType) ?? (effectiveInitialTab === 'tagihan' && !searchParams.get('filter') ? 'periode' : 'semua')
  const initialStatus = (searchParams.get('status') as IuranStatus) || null
  const initialPembayaran = (searchParams.get('pembayaran') as PaymentFilterStatus) || null
  const initialPeriode = searchParams.get('periode') ?? (effectiveInitialTab === 'tagihan' ? currentYear : '')
  const initialJenis = searchParams.get('jenis') ?? ''
  const initialPage = parseInt(searchParams.get('page') || '1', 10) || 1
  const initialPageSize = parseInt(searchParams.get('page_size') || searchParams.get('per_page') || '', 10) || undefined

  const [filterType, setFilterType] = useState<FilterType>(initialFilter)
  const [search, setSearch] = useState(initialSearch)
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch)
  const [statusValue, setStatusValue] = useState<IuranStatus | null>(initialStatus)
  const [pembayaranValue, setPembayaranValue] = useState<PaymentFilterStatus | null>(initialPembayaran)
  const [periodeValue, setPeriodeValue] = useState<string>(initialPeriode)
  const [jenisValue, setJenisValue] = useState<string>(initialJenis)

  // Dropdown menu visibility state
  const [statusOpen, setStatusOpen] = useState(false)
  const [pembayaranOpen, setPembayaranOpen] = useState(false)
  const [periodeOpen, setPeriodeOpen] = useState(false)
  const [jenisOpen, setJenisOpen] = useState(false)

  const statusDropdownRef = useRef<HTMLDivElement>(null)
  const pembayaranDropdownRef = useRef<HTMLDivElement>(null)
  const periodeDropdownRef = useRef<HTMLDivElement>(null)
  const jenisDropdownRef = useRef<HTMLDivElement>(null)

  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = usePersistedPageSize('social-finance:table-page-size:iuran', [10, 25, 50, 100], initialPageSize)

  // Debounce search input by 300ms to prevent duplicate in-flight requests
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Sync activeTab + filter state to URL for persistence across refreshes
  useEffect(() => {
    const params = new URLSearchParams()
    if (activeTab === 'kelola-iuran') params.set('tab', 'kelola-iuran')
    else if (activeTab === 'list-iuran') params.set('tab', 'list-iuran')
    // "Iuran Saya" is the default tab, so it needs no explicit URL parameter.
    if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim())
    if (filterType !== 'semua') params.set('filter', filterType)
    if (statusValue) params.set('status', statusValue)
    if (pembayaranValue) params.set('pembayaran', pembayaranValue)
    if (periodeValue) params.set('periode', periodeValue)
    if (jenisValue) params.set('jenis', jenisValue)
    if (page > 1) params.set('page', String(page))
    if (pageSize) params.set('page_size', String(pageSize))

    setSearchParams(params, { replace: true })
  }, [searchParams, debouncedSearch, filterType, statusValue, pembayaranValue, periodeValue, jenisValue, page, pageSize, activeTab, setSearchParams])

  // Close all dropdown menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setStatusOpen(false)
      }
      if (pembayaranDropdownRef.current && !pembayaranDropdownRef.current.contains(target)) {
        setPembayaranOpen(false)
      }
      if (periodeDropdownRef.current && !periodeDropdownRef.current.contains(target)) {
        setPeriodeOpen(false)
      }
      if (jenisDropdownRef.current && !jenisDropdownRef.current.contains(target)) {
        setJenisOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchedPaymentBillIds = useRef<Set<string>>(new Set())
  const requestIdRef = useRef(0)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  // Map backend bill to frontend IuranBill format
  const mapBill = useCallback((b: ApiBill): IuranBill => {
    const iuranType = b.due_name ?? 'Iuran'
    const householdName = b.head_name ?? b.house_number ?? 'Unknown'
    const rt = b.rt_code ? b.rt_code.replace(/^RT\s*/i, '') : '—'
    const nominal = parseMoney(b.amount)
    const paidAmount = b.paid_amount ? parseMoney(b.paid_amount) : 0
    const pendingAmount = b.pending_amount ? parseMoney(b.pending_amount) : 0
    const isVoluntary = Boolean(b.is_voluntary || nominal === 0)
    const isObligatory = b.is_obligatory !== false
    // Prioritas utama: jika ada pembayaran yang menunggu verifikasi → status tersendiri
    let status: IuranStatus
    if (isVoluntary && nominal === 0) {
      status = 'sukarela'
    } else if (pendingAmount > 0) {
      status = 'menunggu_verifikasi'
    } else {
      status = (BACKEND_STATUS_MAP[b.status] ?? 'belum_bayar')
    }

    return {
      id: b.id,
      householdId: b.household_occupancy_id,
      householdName,
      houseNumber: b.house_number ?? undefined,
      rt,
      iuranType,
      periode: b.period,
      nominal,
      paidAmount,
      pendingAmount,
      status,
      isVoluntary,
      isObligatory,
      moveInDate: b.move_in_date ?? undefined,
      moveOutDate: b.move_out_date ?? undefined,
      isActive: b.is_active !== false,
      dueId: b.due_id,
      dueType: (b.due_type as DueType) ?? (isVoluntary ? 'voluntary' : 'regular'),
      year: b.due_year ?? undefined,
      payments: [],
    }
  }, [])

  const loadData = useCallback(async () => {
    const currentReq = ++requestIdRef.current
    setLoading(true)
    setError(null)
    fetchedPaymentBillIds.current.clear()

    try {
      const token = getSessionPair()?.accessToken
      if (!token) {
        setError('Sesi Anda telah berakhir. Silakan login ulang.')
        setLoading(false)
        return
      }

      let apiStatus: string | undefined
      if (statusValue === 'lunas') apiStatus = 'paid'
      else if (statusValue === 'belum_bayar') apiStatus = 'unpaid'
      else if (statusValue === 'dibatalkan') apiStatus = 'cancelled'
      else if (statusValue === 'sebagian') apiStatus = 'partial'

      const resp = await apiListBills(token, {
        page,
        page_size: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: apiStatus,
        payment_status: pembayaranValue || undefined,
        period: periodeValue || undefined,
        due_name: jenisValue || undefined,
        my_bills: activeTab === 'tagihan' ? true : undefined,
        // Iuran Warga: ask the backend for a global order (unpaid/partial oldest-first,
        // then paid) so tier priority holds across pages, not just within one page.
        order: activeTab === 'list-iuran' ? 'unpaid_first' : undefined,
      })

      if (currentReq !== requestIdRef.current) {
        return
      }

      const bills = resp?.data?.map(mapBill) ?? []
      setAllBills(bills)

      const serverTotal = resp.pagination?.total ?? bills.length
      const serverTotalPages = resp.pagination?.total_pages ?? Math.max(1, Math.ceil(serverTotal / pageSize))
      setTotal(serverTotal)
      setTotalPages(serverTotalPages)

      if (serverTotalPages > 0 && page > serverTotalPages) {
        setPage(serverTotalPages)
      }
    } catch (err) {
      if (currentReq === requestIdRef.current) {
        setAllBills([])
        setError(err instanceof Error ? err.message : 'Gagal memuat data')
      }
    } finally {
      if (currentReq === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [page, pageSize, debouncedSearch, statusValue, pembayaranValue, periodeValue, jenisValue, activeTab, mapBill])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // Auto-refresh when user returns to tab
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && activeTab !== 'list-iuran') {
        void loadData()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [loadData])

  // Fetch semua periode untuk dropdown filter (tidak terpadu dengan halaman)
  const fetchAllPeriodes = useCallback(async () => {
    const token = getSessionPair()?.accessToken
    if (!token) return
    let apiStatus: string | undefined
    if (statusValue === 'lunas') apiStatus = 'paid'
    else if (statusValue === 'belum_bayar') apiStatus = 'unpaid'
    else if (statusValue === 'dibatalkan') apiStatus = 'cancelled'
    else if (statusValue === 'sebagian') apiStatus = 'partial'
    try {
      const periodes = new Set<string>()
      let currentPage = 1
      let totalPages = 1
      while (currentPage <= totalPages) {
        const resp = await apiListBills(token, {
          page: currentPage,
          page_size: 100,
          search: debouncedSearch.trim() || undefined,
          status: apiStatus,
          payment_status: pembayaranValue || undefined,
          due_name: jenisValue || undefined,
          my_bills: activeTab === 'tagihan' ? true : undefined,
        })
        const bills = resp?.data?.map(mapBill) ?? []
        bills.forEach((b) => { if (b.periode) periodes.add(b.periode) })
        totalPages = resp.pagination?.total_pages ?? 1
        currentPage++
      }
      setAllPeriodesSet(periodes)
    } catch (err) {
      // Silently ignore — dropdown akan menampilkan periode yang tersedia
    }
  }, [debouncedSearch, statusValue, pembayaranValue, jenisValue, activeTab, mapBill])

  useEffect(() => {
    void fetchAllPeriodes()
  }, [fetchAllPeriodes])

  // Load dues for "Kelola Iuran" tab
  const loadDues = useCallback(async () => {
    const currentReq = ++requestIdRef.current
    setDuesLoading(true)
    setDuesError(null)
    try {
      const token = getSessionPair()?.accessToken
      if (!token) {
        setDuesError('Sesi Anda telah berakhir. Silakan login ulang.')
        setDuesLoading(false)
        return
      }
      // Kelola Iuran must also expose inactive dues so managers can
      // reactivate them from the edit form.
      const resp = await apiListDues(token)
      if (currentReq !== requestIdRef.current) return
      if (!resp || !Array.isArray(resp) || resp.length === 0) {
        if (resp && resp.length === 0) {
          setDues([])
          setDuesLoading(false)
          return
        }
        throw new Error('Gagal memuat konfigurasi iuran')
      }
      setDues(resp)
    } catch (err) {
      if (currentReq === requestIdRef.current) {
        setDues([])
        setDuesError(err instanceof Error ? err.message : 'Gagal memuat data')
      }
    } finally {
      if (currentReq === requestIdRef.current) {
        setDuesLoading(false)
      }
    }
  }, [])

  // Load dues when "Kelola Iuran" tab is activated
  useEffect(() => {
    if (activeTab === 'kelola-iuran') {
      void loadDues()
    }
  }, [activeTab, loadDues])

  // Client-side filter for Kelola Iuran: search by name, filter by period_type
  // (Due model lacks server-side search/filter; the backend only filters is_active)
  const filteredDues = useMemo(() => {
    if (dues.length === 0) return dues
    return dues.filter((due) => {
      // Search: match against due name (case-insensitive)
      if (debouncedSearch.trim()) {
        const q = debouncedSearch.toLowerCase()
        if (!due.name.toLowerCase().includes(q)) return false
      }
      // Filter by period_type (FREKUENSI)
      if (filterType === 'jenis' && jenisValue) {
        const expectedType = jenisValue === 'bulanan' ? 'monthly'
          : jenisValue === 'tahunan' ? 'yearly'
          : 'one_time'
        if (due.period_type !== expectedType) return false
      }
      return true
    })
  }, [dues, debouncedSearch, filterType, jenisValue])

  // Sort logic differs by tab:
  // "Iuran Saya" (tagihan): 5-tier priority (pending → belum → sebagian → lunas → sukarela)
  // "Iuran Warga" (list-iuran): 3-tier — pending, then unpaid/partial (oldest period first),
  //                            then paid (oldest period first).
  const filteredBills = useMemo(() => {
    const sorted = [...allBills]

    // Periode can arrive in two shapes depending on the data source:
    //   1. "2026-01"        → canonical value stored in `bills.period`
    //   2. "Januari 2026"   → legacy/display label
    // Both must be parsed, otherwise every bill collapses to [0,0] and the
    // period ordering silently becomes a no-op.
    const parsePeriod = (p: string): [number, number] => {
      if (!p) return [0, 0]

      // Shape 1: YYYY-MM (also tolerate YYYY-M and YYYY/MM)
      const ym = /^(\d{4})[-/](\d{1,2})$/.exec(p.trim())
      if (ym) {
        return [parseInt(ym[1], 10) || 0, parseInt(ym[2], 10) || 0]
      }

      // Shape 2: "<MonthName> <YYYY>"
      const parts = p.trim().split(/\s+/)
      if (parts.length < 2) return [0, 0]
      const year = parseInt(parts[1], 10) || 0
      const monthMap: Record<string, number> = {
        januari: 1, februari: 2, maret: 3, april: 4, mei: 5, juni: 6,
        juli: 7, agustus: 8, september: 9, oktober: 10, november: 11, desember: 12,
        jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, agu: 8, agt: 8,
        sep: 9, okt: 10, nov: 11, des: 12,
      }
      return [year, monthMap[parts[0].toLowerCase()] || 0]
    }

    if (activeTab === 'list-iuran') {
      // Iuran Warga: 3-tier sort
      // Tier 1: menunggu_verifikasi → always top
      // Tier 2: belum_bayar / sebagian / dibatalkan → period oldest-first
      // Tier 3: lunas / sukarela → period oldest-first
      const iuranTier: Record<string, number> = {
        menunggu_verifikasi: 0,
        belum_bayar: 1,
        sebagian: 1,
        dibatalkan: 1,
        lunas: 2,
        sukarela: 2,
      }
      sorted.sort((a, b) => {
        const aTier = iuranTier[a.status] ?? 99
        const bTier = iuranTier[b.status] ?? 99
        if (aTier !== bTier) return aTier - bTier

        const aP = parsePeriod(a.periode)
        const bP = parsePeriod(b.periode)
        const yearCmp = aP[0] - bP[0]
        if (yearCmp !== 0) return yearCmp
        return aP[1] - bP[1]
      })
    } else {
      // Iuran Saya + Kelola Iuran: 5-tier priority
      const tierOrder: Record<IuranStatus, number> = {
        menunggu_verifikasi: 0,
        belum_bayar: 1,
        sebagian: 2,
        lunas: 3,
        dibatalkan: 4,
        sukarela: 5,
      }
      sorted.sort((a, b) => {
        const tierCmp = tierOrder[a.status] - tierOrder[b.status]
        if (tierCmp !== 0) return tierCmp

        const aP = parsePeriod(a.periode)
        const bP = parsePeriod(b.periode)
        const yearCmp = aP[0] - bP[0]
        if (yearCmp !== 0) {
          return (a.status === 'lunas' || a.status === 'sukarela')
            ? bP[0] - aP[0] : yearCmp
        }
        const monthCmp = aP[1] - bP[1]
        return (a.status === 'lunas' || a.status === 'sukarela') ? -monthCmp : monthCmp
      })
    }

    return sorted
  }, [allBills, activeTab])

  const displayTotal = total > 0 ? total : allBills.length
  const displayTotalPages = totalPages > 0 ? totalPages : Math.max(1, Math.ceil(displayTotal / pageSize))
  // IMPORTANT: derive from `filteredBills` (sorted), NOT `allBills` (raw server order).
  // Rendering raw `allBills` here silently discards the tab-specific sort.
  const paginatedBills = useMemo(() => {
    if (filteredBills.length > pageSize) {
      return filteredBills.slice((page - 1) * pageSize, page * pageSize)
    }
    return filteredBills
  }, [filteredBills, page, pageSize])

  // Load payments for visible bills on active page to calculate paidAmount & pendingAmount
  useEffect(() => {
    let cancelled = false
    const token = getSessionPair()?.accessToken
    // BillList already returns paid_amount and pending_amount for Iuran Warga.
    // Avoid one payments request per bill, which can overwhelm RT validation.
    if (activeTab === 'list-iuran' || !token || paginatedBills.length === 0) return

    const unfetched = paginatedBills.filter((b) => !fetchedPaymentBillIds.current.has(b.id))
    if (unfetched.length === 0) return

    unfetched.forEach((b) => fetchedPaymentBillIds.current.add(b.id))

    Promise.all(
      unfetched.map(async (b) => {
        const res = await apiListPayments(token, { bill_id: b.id }).catch(() => null)
        return { billId: b.id, data: res }
      })
    ).then((results) => {
      if (cancelled) return
      setAllBills((prev) => {
        const paymentMap = new Map<string, { paid: number; pending: number }>()
        for (const { billId, data } of results) {
          if (data && Array.isArray(data.data) && data.data.length > 0) {
            const approved = data.data.filter((p: { status: string }) => p.status === 'APPROVED')
            const paid = approved.reduce((sum: number, p: { amount: string }) => sum + parseMoney(p.amount), 0)
            const pending = data.data.filter((p: { status: string }) => p.status === 'PENDING')
            const pendingAmt = pending.reduce((sum: number, p: { amount: string }) => sum + parseMoney(p.amount), 0)
            paymentMap.set(billId, { paid, pending: pendingAmt })
          }
        }
        if (paymentMap.size === 0) return prev
        return prev.map((b) => {
          const pm = paymentMap.get(b.id)
          if (pm) {
            return { ...b, paidAmount: pm.paid, pendingAmount: pm.pending }
          }
          return b
        })
      })
    })

    return () => {
      cancelled = true
    }
  }, [paginatedBills, activeTab])

  const handleFilter = (filter: FilterType) => {
    setFilterType(filter)
    setPage(1)
    if (filter === 'semua') {
      setStatusValue(null)
      setPembayaranValue(null)
      setPeriodeValue('')
      setJenisValue('')
    }
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setDebouncedSearch(value)
    setPage(1)
  }

  const handlePage = (p: number) => {
    if (p >= 1 && p <= displayTotalPages) {
      setPage(p)
    }
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }

  const handleStatusSelect = (val: IuranStatus) => {
    setStatusValue(val)
    setPage(1)
  }

  const handlePembayaranSelect = (val: PaymentFilterStatus) => {
    setPembayaranValue(val)
    setPage(1)
  }

  const handlePeriodeSelect = (val: string) => {
    setPeriodeValue(val)
    setPage(1)
  }

  const handleJenisSelect = (val: string) => {
    setJenisValue(val)
    setPage(1)
  }

  const statusLabel: string | null = statusValue ? getStatusLabel(statusValue) : null
  const pembayaranLabel: string | null = pembayaranValue
    ? PAYMENT_STATUS_OPTIONS.find((o) => o.value === pembayaranValue)?.label ?? null
    : null
  const periodeLabel: string | null = periodeValue ? formatPeriodeLabel(periodeValue) : null
  const jenisLabel: string | null = jenisValue
    ? IURAN_TYPE_OPTIONS.find((o) => o.value === jenisValue)?.label ?? null
    : null

  const uniquePeriodes = useMemo(() => {
    return Array.from(allPeriodesSet).sort().reverse()
  }, [allPeriodesSet])

  const uniquePeriodesOptions = useMemo(() => {
    const opts = uniquePeriodes.map((p) => ({ value: p, label: formatPeriodeLabel(p) }))
    if (!uniquePeriodes.includes(currentYear)) {
      opts.unshift({ value: currentYear, label: `Semua Bulan (${currentYear})` })
    }
    return opts
  }, [uniquePeriodes, currentYear])

  const handleRowClick = useCallback((billId: string) => {
    setShowDetailBillId(billId)
  }, [])

  // ═══════════════════════════════════════════════════
  // Edit Due handlers
  // ═══════════════════════════════════════════════════
  const handleOpenEdit = useCallback((due: ApiDue) => {
    setEditDueId(due.id)
    setEditDueForm({
      name: due.name,
      amount: due.amount,
      period_type: due.period_type,
      payment_rule: due.payment_rule ?? 'full',
      is_voluntary: due.is_voluntary ?? false,
      is_active: due.is_active,
      valid_until: due.valid_until ?? '',
      due_type: due.due_type,
      year: due.year,
    })
    setDueMonths([])
    setDueMonthsError(null)
    setInitialMonthStatus({})
    setPendingMonthChanges({})
    if (due.due_type === 'multi_month') {
      const token = getSessionPair()?.accessToken
      if (token) {
        setDueMonthsLoading(true)
        apiGetDueMonths(token, due.id)
          .then((months) => {
            setDueMonths(months)
            const init: Record<string, boolean> = {}
            for (const m of months) {
              init[m.period] = m.is_active
            }
            setInitialMonthStatus(init)
            setPendingMonthChanges({})
          })
          .catch((err) => setDueMonthsError(err instanceof Error ? err.message : 'Gagal memuat status bulan'))
          .finally(() => setDueMonthsLoading(false))
      }
    }
  }, [])

  const handleCloseEdit = useCallback(() => {
    setEditDueId(null)
    setEditDueForm(null)
    setEditDueLoading(false)
    setDueMonths([])
    setDueMonthsError(null)
    setInitialMonthStatus({})
    setPendingMonthChanges({})
    setSyncBillsMessage(null)
    setShowSyncConfirm(false)
  }, [])

  const handleSyncBills = useCallback(async () => {
    if (!editDueId || !editDueForm) return
    const token = getSessionPair()?.accessToken
    if (!token) return

    setSyncBillsLoading(true)
    setSyncBillsMessage(null)
    setShowSyncConfirm(false)

    try {
      const year = editDueForm.year ?? new Date().getFullYear()
      const res = await apiGenerateBills(
        token,
        {
          due_id: editDueId,
          period: String(year),
          due_date: `${year}-01-10`,
        },
        crypto.randomUUID()
      )
      setSyncBillsMessage(
        res.generated_count > 0
          ? `Sinkronisasi berhasil: ${res.generated_count} tagihan baru diterbitkan untuk KK yang belum memiliki tagihan.`
          : 'Sinkronisasi selesai: Semua KK sudah memiliki tagihan untuk iuran ini.'
      )
      // Reload due months & bills data
      if (editDueForm.due_type === 'multi_month') {
        setDueMonthsLoading(true)
        apiGetDueMonths(token, editDueId)
          .then((months) => {
            setDueMonths(months)
            const init: Record<string, boolean> = {}
            for (const m of months) {
              init[m.period] = m.is_active
            }
            setInitialMonthStatus(init)
            setPendingMonthChanges({})
          })
          .catch(() => {})
          .finally(() => setDueMonthsLoading(false))
      }
      void loadData()
    } catch (err) {
      setSyncBillsMessage(`Gagal menyinkronkan tagihan: ${err instanceof Error ? err.message : 'Terjadi kesalahan'}`)
    } finally {
      setSyncBillsLoading(false)
    }
  }, [editDueId, editDueForm, loadData])

  const handleToggleMonthStatus = useCallback((period: string, currentActive: boolean) => {
    const targetActive = !currentActive
    setDueMonths((prev) =>
      prev.map((m) => (m.period === period ? { ...m, is_active: targetActive } : m))
    )
    setPendingMonthChanges((prev) => {
      const next = { ...prev }
      const initial = initialMonthStatus[period]
      if (initial !== undefined && initial === targetActive) {
        delete next[period]
      } else {
        next[period] = targetActive
      }
      return next
    })
  }, [initialMonthStatus])

  const handleSaveEdit = useCallback(async () => {
    if (!editDueId || !editDueForm) return
    const token = getSessionPair()?.accessToken
    if (!token) return
    setEditDueLoading(true)
    setDueMonthsError(null)
    setDuesError(null)
    try {
      // 1. Simpan perubahan status bulan yang tertunda terlebih dahulu (jika ada)
      const periodsToUpdate = Object.entries(pendingMonthChanges)
      if (periodsToUpdate.length > 0) {
        let successCount = 0
        const failedPeriods: string[] = []
        for (const [period, targetActive] of periodsToUpdate) {
          try {
            await apiUpdateDuePeriodStatus(token, editDueId, period, targetActive)
            successCount++
          } catch {
            failedPeriods.push(period)
          }
        }
        if (failedPeriods.length > 0) {
          setDueMonthsError(
            `Gagal memperbarui status bulan: ${failedPeriods.join(', ')}. ${successCount}/${periodsToUpdate.length} periode berhasil disimpan.`
          )
          // Re-fetch due months to sync server state
          try {
            const freshMonths = await apiGetDueMonths(token, editDueId)
            setDueMonths(freshMonths)
            const freshInit: Record<string, boolean> = {}
            for (const m of freshMonths) {
              freshInit[m.period] = m.is_active
            }
            setInitialMonthStatus(freshInit)
            setPendingMonthChanges({})
          } catch {}
          setEditDueLoading(false)
          return
        }
      }

      // 2. Simpan form utama iuran
      await apiUpdateDue(token, editDueId, {
        ...editDueForm,
        // Empty string explicitly clears a voluntary deadline. Regular dues
        // always send an empty value so backend enforces valid_until = NULL.
        valid_until: editDueForm.is_voluntary ? editDueForm.valid_until : '',
      })
      handleCloseEdit()
      void loadDues()
    } catch (err) {
      // keep modal open — error shown in duesError
      setDuesError(err instanceof Error ? err.message : 'Gagal menyimpan perubahan')
    } finally {
      setEditDueLoading(false)
    }
  }, [editDueId, editDueForm, pendingMonthChanges, loadDues, loadData, handleCloseEdit])

  // ═══════════════════════════════════════════════════
  // Deactivate due handlers
  // ═══════════════════════════════════════════════════
  const handleConfirmDeactivate = useCallback(async () => {
    const token = getSessionPair()?.accessToken
    if (!token || !deactivateDueId) return
    setDeactivateLoading(true)
    try {
      await apiDeactivateDue(token, deactivateDueId)
      setDeactivateDueId(null)
      void loadDues()
    } catch (err) {
      setDuesError(err instanceof Error ? err.message : 'Gagal menonaktifkan iuran')
    } finally {
      setDeactivateLoading(false)
    }
  }, [deactivateDueId, loadDues])

  const handleCloseDeactivate = useCallback(() => {
    setDeactivateDueId(null)
    setDeactivateLoading(false)
  }, [])

  return (
    <>
      {/* PageHeader — separate floating surface */}
      <div className="sf-page-header-surface">
        <div className="sf-page-header-left">
          <span className="sf-page-header-icon">
            <DollarOutlined style={{ fontSize: 14 }} />
          </span>
          <div className="sf-page-header-copy">
            <div className="sf-page-header-title-text">Iuran</div>
            <div className="sf-page-header-subtitle">
              Manajemen tagihan dan pembayaran iuran warga
            </div>
          </div>
        </div>
      </div>

      {/* Content surface */}
      <div className="sf-content-surface">
        {/* Toolbar */}
        <div className="sf-list-toolbar">
          <div className="sf-list-toolbar-left">
            {/* Filter type dropdown */}
            <FilterDropdown
              value={filterType}
              options={statusFilterOptions}
              onChange={handleFilter}
              ariaLabel="Tipe filter"
            />

            {/* Search */}
            <SearchBox
              value={search}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              placeholder="Cari nama tagihan..."
              statusControl={
                filterType === 'status' ? (
                  <div className="sf-status-selector">
                    {statusLabel ? (
                      <span className="sf-status-badge sf-status-badge__active">
                        {statusLabel}
                        <button
                          type="button"
                          className="sf-status-badge-clear"
                          onClick={() => { setStatusValue(null); setPage(1); }}
                          aria-label="Hapus filter status"
                          title="Hapus filter status"
                        >
                          <CloseOutlined style={{ fontSize: 10 }} />
                        </button>
                      </span>
                    ) : (
                      <div className="sf-status-dropdown" ref={statusDropdownRef}>
                        <button
                          type="button"
                          className="sf-status-selector-trigger"
                          onClick={(e) => { e.stopPropagation(); setStatusOpen(!statusOpen) }}
                          aria-haspopup="listbox"
                          aria-expanded={statusOpen}
                        >
                          Pilih Status
                          <DownOutlined style={{ fontSize: 9, color: 'var(--sf-text-muted)' }} />
                        </button>
                        {statusOpen && (
                          <div className="sf-status-dropdown-menu" role="listbox">
                            {STATUS_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`sf-status-dropdown-item ${statusValue === opt.value ? 'sf-status-dropdown-item-active' : ''}`}
                                onClick={() => { handleStatusSelect(opt.value); setStatusOpen(false) }}
                                role="button"
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : filterType === 'pembayaran' ? (
                  <div className="sf-status-selector">
                    {pembayaranLabel ? (
                      <span
                        className="sf-status-badge sf-status-badge__active"
                        style={{
                          color:
                            pembayaranValue === 'pending'
                              ? '#ff9830'
                              : pembayaranValue === 'approved'
                              ? '#73bf69'
                              : '#f2495c',
                        }}
                      >
                        {pembayaranLabel}
                        <button
                          type="button"
                          className="sf-status-badge-clear"
                          onClick={() => { setPembayaranValue(null); setPage(1); }}
                          aria-label="Hapus filter pembayaran"
                          title="Hapus filter pembayaran"
                        >
                          <CloseOutlined style={{ fontSize: 10 }} />
                        </button>
                      </span>
                    ) : (
                      <div className="sf-status-dropdown" ref={pembayaranDropdownRef}>
                        <button
                          type="button"
                          className="sf-status-selector-trigger"
                          onClick={(e) => { e.stopPropagation(); setPembayaranOpen(!pembayaranOpen) }}
                          aria-haspopup="listbox"
                          aria-expanded={pembayaranOpen}
                        >
                          Pilih Status Pembayaran
                          <DownOutlined style={{ fontSize: 9, color: 'var(--sf-text-muted)' }} />
                        </button>
                        {pembayaranOpen && (
                          <div className="sf-status-dropdown-menu" role="listbox">
                            {PAYMENT_STATUS_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`sf-status-dropdown-item ${pembayaranValue === opt.value ? 'sf-status-dropdown-item-active' : ''}`}
                                onClick={() => { handlePembayaranSelect(opt.value); setPembayaranOpen(false) }}
                                role="button"
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : filterType === 'periode' ? (
                  <div className="sf-status-selector">
                    {periodeLabel ? (
                      <span className="sf-status-badge sf-status-badge__active" style={{ color: '#3b82f6' }}>
                        {periodeLabel}
                        <button
                          type="button"
                          className="sf-status-badge-clear"
                          onClick={() => { setPeriodeValue(''); setPage(1); }}
                          aria-label="Hapus filter periode"
                          title="Hapus filter periode"
                        >
                          <CloseOutlined style={{ fontSize: 10 }} />
                        </button>
                      </span>
                    ) : (
                      <div className="sf-status-dropdown" ref={periodeDropdownRef}>
                        <button
                          type="button"
                          className="sf-status-selector-trigger"
                          onClick={(e) => { e.stopPropagation(); setPeriodeOpen(!periodeOpen) }}
                          aria-haspopup="listbox"
                          aria-expanded={periodeOpen}
                        >
                          Pilih Periode
                          <DownOutlined style={{ fontSize: 9, color: 'var(--sf-text-muted)' }} />
                        </button>
                        {periodeOpen && (
                          <div className="sf-status-dropdown-menu" role="listbox">
                            {uniquePeriodesOptions.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`sf-status-dropdown-item ${periodeValue === opt.value ? 'sf-status-dropdown-item-active' : ''}`}
                                onClick={() => { handlePeriodeSelect(opt.value); setPeriodeOpen(false) }}
                                role="button"
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : filterType === 'jenis' ? (
                  <div className="sf-status-selector">
                    {jenisLabel ? (
                      <span className="sf-status-badge sf-status-badge__active">
                        {jenisLabel}
                        <button
                          type="button"
                          className="sf-status-badge-clear"
                          onClick={() => { setJenisValue(''); setPage(1); }}
                          aria-label="Hapus filter jenis"
                          title="Hapus filter jenis"
                        >
                          <CloseOutlined style={{ fontSize: 10 }} />
                        </button>
                      </span>
                    ) : (
                      <div className="sf-status-dropdown" ref={jenisDropdownRef}>
                        <button
                          type="button"
                          className="sf-status-selector-trigger"
                          onClick={(e) => { e.stopPropagation(); setJenisOpen(!jenisOpen) }}
                          aria-haspopup="listbox"
                          aria-expanded={jenisOpen}
                        >
                          Pilih Jenis
                          <DownOutlined style={{ fontSize: 9, color: 'var(--sf-text-muted)' }} />
                        </button>
                        {jenisOpen && (
                          <div className="sf-status-dropdown-menu" role="listbox">
                            {IURAN_TYPE_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`sf-status-dropdown-item ${jenisValue === opt.value ? 'sf-status-dropdown-item-active' : ''}`}
                                onClick={() => { handleJenisSelect(opt.value); setJenisOpen(false) }}
                                role="button"
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null
              }
            />

            {/* No filter — Kelola Iuran shows all active dues */}
          </div>

          {/* Create Due and Waste Truck buttons */}
          <div className="sf-list-toolbar-right" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* "+ Iuran" only shown in "Kelola Iuran" tab */}
            {activeTab === 'kelola-iuran' && (
              <button
                type="button"
                className="sf-create-btn"
                onClick={() => setShowCreateDue(true)}
                aria-label="+ Iuran"
              >
                <PlusOutlined style={{ marginRight: 0, fontSize: 12 }} />
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation: Iuran Saya → Iuran Warga → Kelola Iuran */}
        {/* Hidden for warga — they only see their own bills directly */}
        {!userIsWarga && hasIuranAccess && (
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--sf-border)', marginBottom: '0px' }}>
            <button
              type="button"
              onClick={() => { setActiveTab('tagihan'); setPage(1); }}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === 'tagihan' ? 600 : 400,
                color: activeTab === 'tagihan' ? 'var(--sf-accent)' : 'var(--sf-text-muted)',
                borderBottom: activeTab === 'tagihan' ? '2px solid var(--sf-accent)' : '2px solid transparent',
                background: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: 'none',
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              Iuran Saya
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('list-iuran'); setPage(1); }}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === 'list-iuran' ? 600 : 400,
                color: activeTab === 'list-iuran' ? 'var(--sf-accent)' : 'var(--sf-text-muted)',
                borderBottom: activeTab === 'list-iuran' ? '2px solid var(--sf-accent)' : '2px solid transparent',
                background: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: 'none',
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              Iuran Warga
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('kelola-iuran'); setPage(1); }}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === 'kelola-iuran' ? 600 : 400,
                color: activeTab === 'kelola-iuran' ? 'var(--sf-accent)' : 'var(--sf-text-muted)',
                borderBottom: activeTab === 'kelola-iuran' ? '2px solid var(--sf-accent)' : '2px solid transparent',
                background: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: 'none',
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              Kelola Iuran
            </button>
          </div>
        )}

        {/* Create Due Modal */}
        {showCreateDue && (
          <DueCreate
            onClose={() => setShowCreateDue(false)}
            onSaved={() => { void loadData(); void loadDues(); }}
          />
        )}

        {/* Detail Modal */}
        {showDetailBillId && (
          <IuranDetail
            billId={showDetailBillId}
            open
            onClose={() => setShowDetailBillId(null)}
            onStatusChange={loadData}
            onPayment={(billId) => {
              setShowDetailBillId(null)
              setShowPaymentBillId(billId)
            }}
          />
        )}

        {/* Payment Modal */}
        {showPaymentBillId && (
          <IuranPayment
            billId={showPaymentBillId}
            open={true}
            onClose={() => setShowPaymentBillId(null)}
            onPaymentSuccess={() => { void loadData(); void loadDues(); }}
          />
        )}

        {/* Edit Due Modal */}
        {editDueId && editDueForm && (
          <div className="sf-modal-overlay" role="dialog" aria-modal="true">
            <div className="sf-modal" style={{ width: 480, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}>
              <ModalHeader
                title="Ubah Iuran"
                subtitle="Perubahan akan berlaku untuk tagihan baru yang dibuat setelah ini."
                onClose={handleCloseEdit}
              />
              <ModalBody>
                <div className="sf-form-field">
                  <label className="sf-form-label" htmlFor="edit-due-name">Nama Iuran</label>
                  <input
                    id="edit-due-name"
                    className="sf-form-input"
                    type="text"
                    value={editDueForm.name}
                    onChange={(e) => setEditDueForm({ ...editDueForm, name: e.target.value })}
                    placeholder="Nama iuran"
                  />
                </div>
                <div className="sf-form-field">
                  <label className="sf-form-label" htmlFor="edit-due-amount">Nominal</label>
                  <input
                    id="edit-due-amount"
                    className="sf-form-input"
                    type="text"
                    value={editDueForm.amount}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '')
                      setEditDueForm({ ...editDueForm, amount: raw || '0' })
                    }}
                    placeholder="0"
                  />
                </div>
                {editDueForm.due_type !== 'multi_month' && (
                  <div className="sf-form-field">
                    <label className="sf-form-label" htmlFor="edit-due-period">Frekuensi</label>
                    <select
                      id="edit-due-period"
                      className="sf-form-select"
                      value={editDueForm.period_type}
                      onChange={(e) => setEditDueForm({ ...editDueForm, period_type: e.target.value as 'monthly' | 'yearly' | 'one_time' })}
                    >
                      <option value="monthly">Bulanan</option>
                      <option value="yearly">Tahunan</option>
                      <option value="one_time">Sekali Waktu</option>
                    </select>
                  </div>
                )}
                <div className="sf-form-field">
                  <label className="sf-form-label" htmlFor="edit-due-rule">Aturan Pembayaran</label>
                  <select
                    id="edit-due-rule"
                    className="sf-form-select"
                    value={editDueForm.payment_rule}
                    onChange={(e) => setEditDueForm({ ...editDueForm, payment_rule: e.target.value as 'full' | 'partial' })}
                  >
                    <option value="full">Seluruh</option>
                    <option value="partial">Sebagian</option>
                  </select>
                </div>
                {editDueForm.due_type !== 'multi_month' && (
                  <div className="sf-form-field">
                    <label className="sf-form-label" htmlFor="edit-due-status">Status</label>
                    <select
                      id="edit-due-status"
                      className="sf-form-select"
                      value={editDueForm.is_active ? 'true' : 'false'}
                      onChange={(e) => setEditDueForm({ ...editDueForm, is_active: e.target.value === 'true' })}
                    >
                      <option value="true">Aktif</option>
                      <option value="false">Tidak Aktif</option>
                    </select>
                    <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--sf-text-muted)' }}>
                      Iuran tidak aktif tidak dapat menghasilkan tagihan baru. Tagihan dan pembayaran lama tetap tersimpan.
                    </p>
                  </div>
                )}

                {editDueForm.due_type === 'multi_month' && (
                  <div className="sf-form-field" style={{ marginTop: '20px', borderTop: '1px solid var(--sf-border)', paddingTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label className="sf-form-label" style={{ fontWeight: 600, margin: 0 }}>
                        Kelola Status 12 Bulan ({editDueForm.year ?? 'Tahun Berjalan'})
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {dueMonthsLoading && <span style={{ fontSize: '12px', color: 'var(--sf-text-muted)' }}>Memuat...</span>}
                        <button
                          type="button"
                          className="sf-btn-secondary"
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                          disabled={syncBillsLoading || dueMonthsLoading}
                          onClick={() => setShowSyncConfirm(true)}
                          title="Terbitkan tagihan bagi KK baru yang belum memiliki tagihan pada tahun ini"
                        >
                          {syncBillsLoading ? 'Menyinkronkan...' : '🔄 Sinkronkan Tagihan KK'}
                        </button>
                      </div>
                    </div>
                    {syncBillsMessage && (
                      <div style={{
                        color: syncBillsMessage.startsWith('Gagal') ? 'var(--sf-danger)' : 'var(--sf-success, #1a7f37)',
                        fontSize: '12px',
                        marginBottom: '8px',
                        padding: '6px 10px',
                        background: syncBillsMessage.startsWith('Gagal') ? 'var(--sf-danger-soft, #fdf2f2)' : 'var(--sf-success-soft, #e6f7ed)',
                        borderRadius: '4px',
                        border: `1px solid ${syncBillsMessage.startsWith('Gagal') ? 'var(--sf-danger-border, #fca5a5)' : 'var(--sf-success-border, #a7f3d0)'}`
                      }}>
                        {syncBillsMessage}
                      </div>
                    )}
                    {dueMonthsError && (
                      <div style={{ color: 'var(--sf-danger)', fontSize: '12px', marginBottom: '8px', padding: '6px 10px', background: 'var(--sf-danger-soft)', borderRadius: '4px' }}>
                        {dueMonthsError}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', maxHeight: '220px', overflowY: 'auto', padding: '4px' }}>
                      {dueMonths.map((m) => {
                        const monthNames = [
                          'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
                          'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
                        ]
                        const name = monthNames[m.month - 1] || m.period
                        const isPaidAll = m.paid_bills > 0 && m.paid_bills === m.total_bills

                        return (
                          <div
                            key={m.period}
                            style={{
                              border: '1px solid var(--sf-border)',
                              borderRadius: '6px',
                              padding: '8px',
                              background: m.is_active ? 'var(--sf-bg-surface)' : 'var(--sf-bg-subtle)',
                              opacity: m.is_active ? 1 : 0.75,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', fontWeight: 600 }}>{name}</span>
                              <Badge variant={m.is_active ? 'green' : 'gray'} style={{ fontSize: '10px', padding: '0 6px', height: '18px' }}>
                                {m.is_active ? 'Aktif' : 'Nonaktif'}
                              </Badge>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--sf-text-muted)' }}>
                              {m.paid_bills > 0 ? `${m.paid_bills} Lunas` : `${m.total_bills} Tagihan`}
                            </div>
                            <button
                              type="button"
                              className={m.is_active ? 'sf-btn-secondary' : 'sf-btn-primary'}
                              style={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                marginTop: '4px',
                                alignSelf: 'stretch',
                                height: '24px',
                              }}
                              disabled={dueMonthsLoading || editDueLoading || isPaidAll}
                              title={isPaidAll ? 'Tagihan yang sudah lunas tidak dapat dinonaktifkan' : undefined}
                              onClick={() => handleToggleMonthStatus(m.period, m.is_active)}
                            >
                              {isPaidAll ? 'Lunas' : m.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <button
                  type="button"
                  className="sf-btn-secondary"
                  onClick={handleCloseEdit}
                  disabled={editDueLoading}
                  style={{ marginRight: 'auto' }}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="sf-btn-primary"
                  onClick={handleSaveEdit}
                  disabled={editDueLoading || !editDueForm.name.trim() || !editDueForm.amount}
                >
                  {editDueLoading ? 'Menyimpan...' : 'Simpan'}
                </button>
              </ModalFooter>
            </div>
          </div>
        )}

        {/* Sync Bills Confirmation */}
        {showSyncConfirm && editDueForm && (
          <div className="sf-modal-overlay" role="dialog" aria-modal="true" style={{ zIndex: 1100 }}>
            <div className="sf-modal" style={{ width: 440, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}>
              <ModalHeader
                title="Konfirmasi Sinkronisasi Tagihan KK"
                onClose={() => setShowSyncConfirm(false)}
              />
              <ModalBody>
                <p style={{ fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                  Sistem akan menerbitkan 12 tagihan ({editDueForm.year ?? 'Tahun Berjalan'}) untuk setiap KK yang belum memiliki tagihan pada iuran ini.
                </p>
                <p style={{ fontSize: '12px', color: 'var(--sf-text-muted)', marginTop: '8px', lineHeight: 1.5 }}>
                  Tagihan lama, status aktif/nonaktif, dan riwayat pembayaran yang sudah ada <strong>tidak akan diubah atau diduplikasi</strong>.
                </p>
              </ModalBody>
              <ModalFooter>
                <button
                  type="button"
                  className="sf-btn-secondary"
                  onClick={() => setShowSyncConfirm(false)}
                  disabled={syncBillsLoading}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="sf-btn-primary"
                  onClick={handleSyncBills}
                  disabled={syncBillsLoading}
                >
                  {syncBillsLoading ? 'Menyinkronkan...' : 'Ya, Sinkronkan'}
                </button>
              </ModalFooter>
            </div>
          </div>
        )}

        {/* Deactivate Confirmation */}
        {deactivateDueId && (
          <div className="sf-modal-overlay" role="dialog" aria-modal="true">
            <div className="sf-modal" style={{ width: 400, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}>
              <ModalHeader
                title="Konfirmasi"
                onClose={handleCloseDeactivate}
              />
              <ModalBody>
                <p style={{ fontSize: '14px', lineHeight: 1.6 }}>
                  Apakah Anda yakin ingin menonaktifkan iuran ini? Iuran tidak akan membuat tagihan baru setelah ditonaktifkan.
                </p>
              </ModalBody>
              <ModalFooter>
                <button
                  type="button"
                  className="sf-btn-secondary"
                  onClick={handleCloseDeactivate}
                  disabled={deactivateLoading}
                  style={{ marginRight: 'auto' }}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="sf-btn-danger"
                  onClick={handleConfirmDeactivate}
                  disabled={deactivateLoading}
                >
                  {deactivateLoading ? 'Menonaktifkan...' : 'Ya, Nonaktifkan'}
                </button>
              </ModalFooter>
            </div>
          </div>
        )}

        {/* ═══ TAB: LIST IURAN & TAGIHAN ═══ */}
        {(activeTab === 'list-iuran' || activeTab === 'tagihan') && (
          <>
            {/* Error */}
            {error && (
              <div className="sf-error-banner" role="alert">
                {error}
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="sf-loading-banner">
                Memuat data iuran...
              </div>
            )}

            {/* Empty */}
            {!loading && !error && filteredBills.length === 0 && (
              <div className="sf-empty-banner">
                {search.trim() ? 'Tidak ada data iuran.' : 'Belum ada tagihan.'}
              </div>
            )}

            {/* Bill Table */}
            {!loading && filteredBills.length > 0 && (
              <div className="table-wrapper sf-auto-layout-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="sf-auto-layout-table" style={{ minWidth: '1050px', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap', minWidth: '180px' }}>NAMA WARGA</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '60px', minWidth: '60px' }}>RT</th>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap', minWidth: '150px' }}>NAMA IURAN</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '120px', minWidth: '120px' }}>JENIS IURAN</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '160px', minWidth: '160px' }}>PERIODE</th>
                      <th style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace', width: '140px', minWidth: '130px' }}>NOMINAL</th>
                      <th style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace', width: '130px', minWidth: '120px' }}>DIBAYAR</th>
                      <th style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace', width: '130px', minWidth: '120px' }}>TERTUNDA</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '130px', minWidth: '120px' }}>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBills.map((bill, _idx) => {
                      const hasPending = (bill.pendingAmount ?? 0) > 0

                      return (
                        <tr
                          key={bill.id}
                          onClick={() => handleRowClick(bill.id)}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleRowClick(bill.id)
                            }
                          }}
                        >
                          <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 600 }}>{bill.householdName}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {bill.rt}
                          </td>
                          <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {bill.iuranType}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Badge variant={getDueTypeVariant(bill.dueType)}>
                              {getDueTypeLabel(bill.dueType)}
                            </Badge>
                          </td>
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {bill.periode.includes('-') ? formatPeriodeLabel(bill.periode) : bill.periode}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                            {formatRupiah(bill.nominal)}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                            <span style={{ color: (bill.paidAmount ?? 0) > 0 ? 'var(--sf-success)' : undefined, fontWeight: (bill.paidAmount ?? 0) > 0 ? 600 : 400 }}>
                              {formatRupiah(bill.paidAmount)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                            {hasPending ? (
                              <span style={{ color: 'var(--sf-warning)', fontWeight: 600 }}>
                                {formatRupiah(bill.pendingAmount)}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {bill.isActive === false ? (
                              <Badge variant="gray">Tidak Aktif</Badge>
                            ) : bill.isObligatory === false ? (
                              <Badge variant="gray">Tidak Wajib</Badge>
                            ) : (
                              <Badge variant={getStatusVariant(bill.status)}>
                                {getStatusLabel(bill.status)}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredBills.length > 0 && (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={displayTotal}
                totalPages={displayTotalPages}
                onPageChange={handlePage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            )}
          </>
        )}

        {/* ═══ TAB: KELOLA IURAN ═══ */}
        {activeTab === 'kelola-iuran' && (
          <>
            {/* Loading */}
            {duesLoading && (
              <div className="sf-loading-banner">
                Memuat konfigurasi iuran...
              </div>
            )}

            {/* Error */}
            {duesError && (
              <div className="sf-error-banner" role="alert">
                {duesError}
              </div>
            )}

            {/* Empty */}
            {!duesLoading && !duesError && filteredDues.length === 0 && (
              <div className="sf-empty-banner">
                Belum ada konfigurasi iuran. Klik "Tambah Iuran" untuk membuat.
              </div>
            )}

            {/* Dues Table — row click opens Edit Due modal */}
            {!duesLoading && filteredDues.length > 0 && (
              <div className="table-wrapper sf-auto-layout-wrapper" style={{ overflowX: 'auto' }}>
                <table className="sf-auto-layout-table" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>NAMA IURAN</th>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap', width: '100px' }}>JENIS IURAN</th>
                      <th style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'monospace', width: '140px' }}>NOMINAL</th>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap', width: '240px' }}>PERIODE</th>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap', width: '80px' }}>FREKUENSI</th>
                      <th style={{ textAlign: 'left', whiteSpace: 'nowrap', width: '80px' }}>ATURAN</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '100px' }}>BERLAKU SAMPAI</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '100px' }}>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDues.map((due) => {
                      const isVoluntary = due.is_voluntary ?? false
                      const periodLabel = due.period_type === 'monthly' ? 'Bulanan'
                        : due.period_type === 'yearly' ? 'Tahunan'
                        : 'Sekali Waktu'
                      const ruleLabel = (due.payment_rule ?? 'full') === 'full' ? 'Seluruh' : 'Sebagian'

                      return (
                        <tr
                          key={due.id}
                          onClick={() => handleOpenEdit(due)}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleOpenEdit(due)
                            }
                          }}
                        >
                          <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 600 }}>{due.name}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Badge variant={getDueTypeVariant(due.due_type)}>
                              {getDueTypeLabel(due.due_type)}
                            </Badge>
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {isVoluntary ? (
                              <span style={{ color: 'var(--sf-info)' }}>Sukarela</span>
                            ) : (
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formatRupiah(parseMoney(due.amount))}
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'left', fontSize: '12px', whiteSpace: 'nowrap', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {due.periods && due.periods.length > 0 ? (
                              <>
                                {due.periods.slice(0, 2).map((p) =>
                                  p.includes('-') ? formatPeriodeLabel(p) : p
                                ).join(', ')}
                                {due.periods.length > 2 && (
                                  <span style={{ color: 'var(--sf-text-muted)', fontSize: '11px', marginLeft: '2px' }}>
                                    · +{due.periods.length - 2} lainnya
                                  </span>
                                )}
                              </>
                            ) : (due as any).period ? (
                              ((due as any).period.includes('-') ? formatPeriodeLabel((due as any).period) : (due as any).period)
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {periodLabel}
                          </td>
                          <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {ruleLabel}
                          </td>
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {isVoluntary
                              ? (due.valid_until ? dayjs(due.valid_until).format('DD/MM/YYYY') : 'Tanpa batas')
                              : '—'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Badge variant={due.is_active ? 'green' : 'gray'}>
                              {due.is_active ? 'Aktif' : 'Tidak Aktif'}
                            </Badge>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
